// src/components/CollectionBadge.tsx
"use client";

import type { Collection } from "@/types/timeline";

interface CollectionBadgeProps {
  collection?: Collection | null;
  onClick?: (collection: Collection) => void;
  size?: "small" | "medium" | "large";
  showIcon?: boolean;
}

const MEDIA_TYPE_ICONS: Record<string, string> = {
  podcast: "🎙️",
  video: "📹",
  photo: "🖼️",
  post: "📝",
};

export default function CollectionBadge({
  collection,
  onClick,
  size = "medium",
  showIcon = true,
}: CollectionBadgeProps) {
  // 🔒 Guard: nothing to render without a collection
  if (!collection) return null;

  const typeTitle = collection.media_type_title?.toLowerCase() ?? "";
  const isMediaTypeBadge = ["podcast", "video", "photo", "post"].includes(
    typeTitle,
  );

  const sizeStyles = {
    small: { fontSize: "0.75rem", padding: "0.125rem 0.375rem" },
    medium: { fontSize: "0.875rem", padding: "0.25rem 0.5rem" },
    large: { fontSize: "1rem", padding: "0.375rem 0.75rem" },
  };

  const common: React.CSSProperties = {
    cursor: onClick ? "pointer" : "default",
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    borderRadius: "9999px",
    fontWeight: 500,
    ...sizeStyles[size],
  };

  if (isMediaTypeBadge) {
    const icon = MEDIA_TYPE_ICONS[typeTitle] || "🏷️";
    return (
      <span
        onClick={() => onClick?.(collection)}
        style={{
          ...common,
          backgroundColor: "rgba(0,0,0,0.08)",
          color: "var(--foreground)",
        }}
      >
        {showIcon && <span>{icon}</span>}
        <span>{collection.title}</span>
      </span>
    );
  }

  return (
    <span
      onClick={() => onClick?.(collection)}
      style={{
        ...common,
        backgroundColor: "transparent",
        color: "var(--link-color, #0066cc)",
        textDecoration: "underline",
        textUnderlineOffset: "2px",
        padding: "0.125rem 0",
      }}
    >
      <span>{collection.title}</span>
    </span>
  );
}
